<script setup lang="ts">
import type { TemplateNode, TextAreaVariable, VTextAreaProps } from "./VTextArea.types";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useSlots, watch } from "vue";
import { useI18n } from "vue-i18n";
import ACTooltip from "../ACTooltip/ACTooltip.vue";
import VDropdown from "../Dropdown/VDropdown.vue";
import VDropdownItem from "../DropdownItem/VDropdownItem.vue";
import { formRequiredCva } from "../Form/form.cva";
import VIcon from "../Icon/VIcon.vue";
import VLanguageSwitch from "../LanguageSwitch/VLanguageSwitch.vue";
import VTranslationNotices from "../LanguageSwitch/VTranslationNotices.vue";
import { useFieldId } from "../useFieldId";
import { nodesAreEmpty, nodesLength, textNode } from "./textAreaNodes";
import VTextAreaNodeEditor from "./VTextAreaNodeEditor.vue";

/**
 * The one multi-line text field: plain text through `v-model`, or text with variable chips through `v-model:nodes`
 * when `variables` are given. Optionally shows a language switch, translation notices, a character count and toolbar
 * slots in a compact bar below the text.
 */
defineOptions({
  name: "VTextArea",
});

const {
  name = "",
  labelTop = "",
  labelLeft = "",
  required = false,
  placeholder = "",
  errorMessage = "",
  invalid = false,
  showErrorBelow = false,
  disabled: disabledProp = false,
  loading = false,
  readonly = false,
  hasPerms = true,
  disabledReason = undefined,
  rows = "3",
  minHeight = "85px",
  maxHeight = "300px",
  resize = "vertical",
  autoResize = false,
  size = "medium",
  hideClear = false,
  spellcheck = true,
  maxLength = undefined,
  showCharacterCount = false,
  variables = undefined,
  insertVariableLabel = undefined,
  languages = [],
  languageGroups = [],
  languageSelectLabel = undefined,
  showTranslateNotice = false,
  translateNoticeText = "",
  translateAutomaticallyLabel = "",
  showOutdatedNotice = false,
  outdatedNoticeText = "",
  refreshLabel = "",
  refreshing = false,
  ariaDescribedby = undefined,
  ariaLabelledby = undefined,
  id: rawId = undefined,
} = defineProps<VTextAreaProps>();

const emit = defineEmits<{
  blur: []
  focus: []
  keydown: [event: KeyboardEvent]
  keyup: [event: KeyboardEvent]
  refresh: []
}>();

const slots = useSlots();
const { t } = useI18n();

const model = defineModel<string>({ default: "" });
const nodes = defineModel<TemplateNode[]>("nodes", { default: () => [] });
const selectedLanguage = defineModel<string>("selectedLanguage", { default: "" });
const translateAutomatically = defineModel<boolean>("translateAutomatically", { default: false });

const requiredClasses = formRequiredCva();

const id = useFieldId(() => rawId);
const textAreaRef = ref<HTMLTextAreaElement | null>(null);
const surfaceRef = ref<HTMLElement | null>(null);
const nodeEditorRef = ref<InstanceType<typeof VTextAreaNodeEditor> | null>(null);

/** With variables the content is a list of text and variable nodes instead of a string. */
const withVariables = computed(() => variables !== undefined);
const variableOptions = computed<TextAreaVariable[]>(() => variables ?? []);

/** A caller-provided disabled reason disables the field like `disabled` does. */
const disabled = computed(() => disabledProp || Boolean(disabledReason));
/** While loading the text stays visible, but nothing in the field can change it until the work is done. */
const canEdit = computed(() => !disabled.value && hasPerms && !readonly && !loading);
const hasLanguages = computed(() => languages.length > 0);
const isEmpty = computed(() => (withVariables.value ? nodesAreEmpty(nodes.value) : !model.value));
const length = computed(() => (withVariables.value ? nodesLength(nodes.value) : model.value.length));

const showToolbar = computed(() =>
  hasLanguages.value
  || variableOptions.value.length > 0
  || !!slots["toolbar-start"]
  || !!slots["toolbar-end"]
  || showCharacterCount,
);
const showFooter = computed(() => showToolbar.value || showTranslateNotice || showOutdatedNotice);
const showResizeHandle = computed(() => resize === "vertical" && !autoResize && canEdit.value);
const computedAriaBusy = computed<"true" | undefined>(() => (loading ? "true" : undefined));

const showErrorUnder = computed(() => showErrorBelow || (!!errorMessage && !isEmpty.value));
const errorId = computed(() => `${id.value}-error`);
const labelId = computed(() => `${id.value}-label`);
// A label's `for` cannot point at the editor with variables, which is not a form control, so it names it by id.
const editorAriaLabelledby = computed(() => ariaLabelledby ?? (labelTop || labelLeft ? labelId.value : undefined));

const computedAriaInvalid = computed<"true" | undefined>(() => (errorMessage || invalid ? "true" : undefined));

const computedAriaDescribedby = computed(() => {
  const parts: string[] = [];
  if (ariaDescribedby) {
    parts.push(ariaDescribedby);
  }
  if (showErrorUnder.value && errorMessage) {
    parts.push(errorId.value);
  }
  return parts.length > 0 ? parts.join(" ") : undefined;
});

const placeholderText = computed(() => (errorMessage && !showErrorUnder.value ? errorMessage : placeholder));

// Same as VInput: 16px on phones so iOS does not zoom in on focus, 14px from `sm` up.
const sizeClass = computed(() => ({
  small: "text-sm",
  medium: "text-base sm:text-sm",
  large: "text-xl",
})[size]);

const surfaceClasses = computed(() => [
  "block w-full px-4 py-2.5 font-roboto bg-transparent focus:outline-hidden overflow-auto",
  sizeClass.value,
  {
    "pb-6": showResizeHandle.value,
    "pr-9": (!hideClear && canEdit.value && !isEmpty.value) || loading,
    "text-danger-500": errorMessage && !showErrorUnder.value,
    "cursor-not-allowed": disabled.value || !hasPerms,
    "cursor-default": readonly && !loading,
    "cursor-progress": loading && !disabled.value && hasPerms,
  },
]);

function onInput(event: Event) {
  const target = event.target as HTMLTextAreaElement;
  let value = target.value;
  if (maxLength !== undefined && value.length > maxLength) {
    value = value.slice(0, maxLength);
    target.value = value;
  }
  model.value = value;
  if (autoResize) {
    fitToContent();
  }
}

function clearValue() {
  if (withVariables.value) {
    nodes.value = [textNode("")];
    nodeEditorRef.value?.focus();
    return;
  }
  model.value = "";
  textAreaRef.value?.focus();
}

function insertVariable(identifier: string) {
  nodeEditorRef.value?.insertVariable(identifier);
}

/** Grows the plain textarea with its content up to `maxHeight`; the variable editor grows by itself. */
function fitToContent() {
  const textArea = textAreaRef.value;
  if (!textArea?.offsetParent) {
    return;
  }
  textArea.style.height = "auto";
  textArea.style.height = `${Math.min(textArea.scrollHeight, Number.parseInt(maxHeight))}px`;
}

let startHeight = 0;
let startY = 0;

function pointerY(event: MouseEvent | TouchEvent) {
  return event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
}

/** The element the handle resizes: the variables editor surface, or the plain textarea. */
function resizeTarget(): HTMLElement | null {
  return withVariables.value ? surfaceRef.value : textAreaRef.value;
}

function startResize(event: MouseEvent | TouchEvent) {
  const target = resizeTarget();
  if (!target || !canEdit.value) {
    return;
  }
  event.preventDefault();
  startHeight = target.offsetHeight;
  startY = pointerY(event);
  window.addEventListener("mousemove", onResize);
  window.addEventListener("mouseup", stopResize);
  window.addEventListener("touchmove", onResize);
  window.addEventListener("touchend", stopResize);
}

function onResize(event: MouseEvent | TouchEvent) {
  const target = resizeTarget();
  if (!target) {
    return;
  }
  const height = Math.max(
    Number.parseInt(minHeight),
    Math.min(startHeight + (pointerY(event) - startY), Number.parseInt(maxHeight)),
  );
  target.style.height = `${Math.round(height)}px`;
}

function stopResize() {
  window.removeEventListener("mousemove", onResize);
  window.removeEventListener("mouseup", stopResize);
  window.removeEventListener("touchmove", onResize);
  window.removeEventListener("touchend", stopResize);
}

watch(model, () => {
  if (autoResize) {
    void nextTick(fitToContent);
  }
});

onMounted(() => {
  if (autoResize) {
    fitToContent();
  }
});

onBeforeUnmount(stopResize);
</script>

<template>
  <div
    class="relative w-full dark:text-white"
    :class="labelLeft ? 'flex flex-row items-start gap-2' : 'flex flex-col'"
  >
    <label
      v-if="labelTop || labelLeft"
      :id="labelId"
      :for="withVariables ? undefined : id"
      class="text-sm leading-4 flex items-center gap-1 dark:text-gray-300"
      :class="labelLeft ? 'w-1/3 pt-3' : 'mb-1'"
    >
      {{ labelTop || labelLeft }}
      <span v-if="required" :class="requiredClasses">*</span>
    </label>

    <div class="flex min-w-0 flex-1 flex-col">
      <div
        class="relative overflow-hidden rounded-[20px] border border-solid bg-white dark:bg-dark-800 transition-colors duration-200"
        :class="[
          errorMessage
            ? 'border-danger-500'
            : invalid
              ? 'border-danger-600'
              : 'border-gray-300 dark:border-dark-600',
          { 'focus-within:border-primary-400 dark:focus-within:border-primary-600': !errorMessage && !invalid && canEdit },
          { 'opacity-50': disabled },
        ]"
        data-test="textarea"
      >
        <ACTooltip type="edit_field" :allow-change="hasPerms" :disabled-reason="disabledReason">
          <div class="relative" :class="{ 'opacity-50 cursor-not-allowed': !hasPerms && !disabled }">
            <div
              v-if="withVariables"
              ref="surfaceRef"
              :class="surfaceClasses"
              :style="{ maxHeight, minHeight, scrollbarGutter: 'stable' }"
            >
              <VTextAreaNodeEditor
                :id="id"
                ref="nodeEditorRef"
                v-model="nodes"
                :variables="variableOptions"
                :editable="canEdit"
                :placeholder="placeholderText"
                :max-length="maxLength"
                :aria-invalid="computedAriaInvalid"
                :aria-describedby="computedAriaDescribedby"
                :aria-labelledby="editorAriaLabelledby"
                :aria-busy="computedAriaBusy"
                @blur="emit('blur')"
                @focus="emit('focus')"
                @keydown="emit('keydown', $event)"
                @keyup="emit('keyup', $event)"
              />
            </div>
            <textarea
              v-else
              :id="id"
              ref="textAreaRef"
              :value="model"
              :name="name"
              :rows="rows"
              :disabled="disabled || !hasPerms || loading"
              :readonly="readonly"
              :placeholder="placeholderText"
              :spellcheck="spellcheck"
              class="resize-none placeholder:text-gray-400 dark:placeholder:text-dark-400"
              :class="[surfaceClasses, { 'placeholder:text-danger-500!': errorMessage && !showErrorUnder }]"
              :style="{ maxHeight, minHeight, scrollbarGutter: 'stable' }"
              :aria-invalid="computedAriaInvalid"
              :aria-describedby="computedAriaDescribedby"
              :aria-labelledby="ariaLabelledby"
              :aria-busy="computedAriaBusy"
              @input="onInput"
              @blur="emit('blur')"
              @focus="emit('focus')"
              @keydown="emit('keydown', $event)"
              @keyup="emit('keyup', $event)"
            />
            <button
              v-if="!isEmpty && canEdit && !hideClear"
              type="button"
              class="absolute right-3 top-2.5 rounded-full text-gray-800 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500"
              :aria-label="t('base.clear')"
              data-test="clear"
              @click="clearValue"
            >
              <VIcon name="x" class="w-4 h-4" aria-hidden="true" />
            </button>
            <span
              v-if="loading"
              class="pointer-events-none absolute right-3 top-2.5 text-gray-400 dark:text-dark-400"
              aria-hidden="true"
              data-test="loading"
            >
              <VIcon name="loader-circle" spin class="w-4 h-4" />
            </span>
            <div
              v-if="showResizeHandle"
              class="absolute bottom-1 right-1 w-6 h-6 cursor-ns-resize touch-none"
              aria-hidden="true"
              @mousedown="startResize"
              @touchstart="startResize"
            >
              <div
                class="absolute bottom-[11px] right-[4px] w-5 h-[2px] rounded-full rotate-135"
                :class="errorMessage ? 'bg-danger-500' : 'bg-gray-300 dark:bg-dark-400'"
              />
              <div
                class="absolute bottom-[6px] right-[4px] w-3 h-[2px] rounded-full rotate-135"
                :class="errorMessage ? 'bg-danger-500' : 'bg-gray-300 dark:bg-dark-400'"
              />
            </div>
          </div>
        </ACTooltip>

        <div
          v-if="showFooter"
          class="border-t border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700"
        >
          <div
            v-if="showTranslateNotice || showOutdatedNotice"
            class="flex flex-col gap-1.5 px-4 pt-2"
            :class="{ 'pb-2': !showToolbar }"
          >
            <VTranslationNotices
              v-model:translate-automatically="translateAutomatically"
              :show-translate-notice="showTranslateNotice"
              :translate-notice-text="translateNoticeText"
              :translate-automatically-label="translateAutomaticallyLabel"
              :show-outdated-notice="showOutdatedNotice"
              :outdated-notice-text="outdatedNoticeText"
              :refresh-label="refreshLabel"
              :refreshing="refreshing"
              :disabled="disabled"
              @refresh="emit('refresh')"
            />
          </div>

          <div
            v-if="showToolbar"
            class="flex items-center justify-between gap-2 px-2 py-0.5"
            data-test="toolbar"
          >
            <div class="flex min-w-0 items-center gap-1">
              <VLanguageSwitch
                v-if="hasLanguages"
                v-model="selectedLanguage"
                :languages="languages"
                :groups="languageGroups"
                :label="languageSelectLabel"
                :disabled="disabled"
              />
              <VDropdown
                v-if="variableOptions.length > 0"
                theme="verbleif-popper"
                popper-class="v-scroll-menu-popper"
                placement="top-start"
                :disabled="!canEdit"
              >
                <button
                  type="button"
                  class="inline-flex h-6 items-center gap-1.5 rounded-full px-2 text-xs font-medium leading-none transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500"
                  :class="canEdit
                    ? 'text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20'
                    : 'cursor-not-allowed text-gray-400 dark:text-gray-500'"
                  :disabled="!canEdit"
                  data-test="insert-variable"
                >
                  <VIcon name="plus" class="text-[10px]" aria-hidden="true" />
                  <span>{{ insertVariableLabel ?? t("base.insert_variable") }}</span>
                  <VIcon name="chevron-down" class="text-[10px]" aria-hidden="true" />
                </button>
                <template #popper="{ hide }">
                  <div class="options v-scroll-menu" data-test="variable-options">
                    <VDropdownItem
                      v-for="variable in variableOptions"
                      :key="variable.identifier"
                      :text="variable.label"
                      icon="code"
                      size="small"
                      :hide="hide"
                      :data-identifier="variable.identifier"
                      :title="variable.description"
                      @click="insertVariable(variable.identifier)"
                    />
                  </div>
                </template>
              </VDropdown>
              <slot name="toolbar-start" :disabled="disabled || !hasPerms || loading" />
            </div>

            <div class="flex shrink-0 items-center gap-2">
              <slot name="toolbar-end" />
              <span
                v-if="showCharacterCount"
                class="text-xs text-gray-400 dark:text-gray-500"
                data-test="character-count"
              >
                {{ length }}{{ maxLength ? ` / ${maxLength}` : '' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="showErrorUnder && errorMessage"
        :id="errorId"
        class="mt-1 text-sm text-danger-500"
        data-test="error-message"
      >
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
textarea,
div[style*="scrollbar-gutter"] {
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--input-border);
    border-radius: 3px;
    border: 1px solid var(--input-background);
  }

  &::-webkit-scrollbar-corner {
    background: transparent;
  }

  scrollbar-width: thin;
  scrollbar-color: var(--input-border) transparent;
}
</style>
